import {CtaButtonColor, CtaButtonVariant, CtaType, DynamicCta} from "@/types/dto/common/dynamic-cta";
import brochureDisplayFactory from "@/factories/brochure/brochure-factory";
import {BasicInfoCta} from "@/types/dto/common/basic-info";
import {useTranslations} from "next-intl";
import promoCodeDataFactory from "../promo-code-factory";

 export const resolveButtonProps = (buttonVariant: BasicInfoCta["buttonVariant"]): {
  variant?: CtaButtonVariant;
  color?: CtaButtonColor;
} => {
  if (buttonVariant === "Outlined") {
    return { variant: "outlined", color: "primary" };
  }

  if (buttonVariant === "Secondary") {
    return { variant: "contained", color: "secondary" };
  }

  if (buttonVariant === "Primary") {
    return { variant: "contained", color: "primary" };
  }

  return { variant: undefined, color: undefined };
};

const internalLinkFactory = (data: BasicInfoCta): DynamicCta<"internalLink"> => ({
  label: data?.callToActionText ?? "",
  ctaType: "internalLink",
  ctaData: {
    url: data?.callToActionUrl ?? ""
  }
});

const externalLinkFactory = (data: BasicInfoCta): DynamicCta<"externalLink"> => ({
  label: data?.callToActionText ?? "",
  ctaType: "externalLink",
  ctaData: {
    url: data?.callToActionUrl ?? "",
    openInNewTab: data?.openInNewTab ?? true
  }
});

const hubspotCtaFactory = (data: BasicInfoCta): DynamicCta<"hubspotFormWithCta"> => ({
  label: data?.callToActionText ?? "",
  ctaType: "hubspotFormWithCta",
  ctaData: {
    formId: data?.hubspotFormInfo?.formId ?? "",
    formName: data?.hubspotFormInfo?.formName ?? "",
    formTitle: data?.hubspotFormInfo?.formTitle ?? "",
    portalId: data?.hubspotFormInfo?.portalId ?? ""
  }
});

const viewBrochureOnlineCtaFactory = (data: BasicInfoCta): DynamicCta<"viewBrochureOnlineForm"> => ({
  label: data?.callToActionText ?? "",
  ctaType: "viewBrochureOnlineForm",
  ctaData: {
    url: data?.callToActionUrl ?? "",
    hubSpotForm: {
      formId: data?.hubspotFormInfo?.formId ?? "",
      formName: data?.hubspotFormInfo?.formName ?? "",
      formTitle: data?.hubspotFormInfo?.formTitle ?? "",
      portalId: data?.hubspotFormInfo?.portalId ?? ""
    }
  }
});

const requestABrochureFactory = (data: BasicInfoCta): DynamicCta<"requestABrochureForm"> => ({
  label: data?.callToActionText ?? "",
  ctaType: "requestABrochureForm",
  ctaData: {
    brochureDisplay: brochureDisplayFactory(data.brochureForm)
  }
});

const promoCodeFactory = (data: BasicInfoCta, t?: ReturnType<typeof useTranslations>): DynamicCta<"promoCode"> => ({
  label: data?.callToActionText ?? "",
  ctaType: "promoCode",
  ctaData: promoCodeDataFactory(data?.promoCode, t)
});

const factoryMap: Record<CtaType, Function> = {
  internalLink: internalLinkFactory,
  externalLink: externalLinkFactory,
  hubspotFormWithCta: hubspotCtaFactory,
  requestABrochureForm: requestABrochureFactory,
  viewBrochureOnlineForm: viewBrochureOnlineCtaFactory,
  promoCode: promoCodeFactory
};

export const dynamicCtaButtonFactory = <T extends CtaType>(
  cta: BasicInfoCta,
  t?: ReturnType<typeof useTranslations>
): DynamicCta<T> => {
  let dynamicCta: DynamicCta<T>;

  if (cta.ctaType === "hubspotFormWithCta" && cta.callToActionUrl && cta.hubspotFormInfo) {
    dynamicCta = factoryMap.viewBrochureOnlineForm(cta) as DynamicCta<T>;
  } else if (cta.ctaType && cta.ctaType in factoryMap) {
    dynamicCta = factoryMap[cta.ctaType as CtaType](cta, t) as DynamicCta<T>;
  } else {
    dynamicCta = internalLinkFactory(cta) as DynamicCta<T>;
  }

  return {
    ...dynamicCta,
    ...resolveButtonProps(cta.buttonVariant)
  };
};

const dynamicCtaButtonListFactory = <T extends CtaType>(
  ctas: BasicInfoCta[],
  t?: ReturnType<typeof useTranslations>
): DynamicCta<T>[] =>
  Array.isArray(ctas) ? ctas.map(cta => dynamicCtaButtonFactory<T>(cta, t)) : [];

export default dynamicCtaButtonListFactory;
